import { useEffect, useState, type ReactNode } from 'react';
import { useT } from '@hana/i18n';
import s from './dockPanel.module.css';

/** Bảng phải dùng chung (board Mint-AR/-Pipeline/-HR… khổ 1920: Phân tích · Việc · Duyệt, Cảnh báo · Hoạt động · Thông báo).
 *  Vỏ: tab dạng segmented có số đếm + nút đóng; nội dung do màn truyền (`children`). Đặt vào `<Page dock={…}>`. */
export interface DockTab<K extends string = string> {
  key: K;
  label: string;
  /** số đếm ở cuối nhãn (ẩn khi 0 / không có) */
  n?: number;
}

export function DockPanel<K extends string>({ tabs, tab, onTab, onClose, closeLabel, top, children }: { tabs: ReadonlyArray<DockTab<K>>; tab: K; onTab: (k: K) => void; onClose: () => void; /** nhãn nút đóng: mặc định 'Đóng bảng'; board Pipeline / Contract / SalesToday / KPI / BOD / Marketing 1920 ghi 'Đóng cột phải' → truyền `t('shell.dockCloseRight')` */ closeLabel?: string; /** khối đặt TRÊN thanh tab (vd Tóm tắt đầu ngày ở Mint-Home-1920) */ top?: ReactNode; children: ReactNode }) {
  const t = useT();
  return (
    <div className={s.dock} aria-label={t('shell.dock')}>
      {top}
      <div className={s.head}>
        <div role="tablist" aria-label={t('shell.dock')} className={s.tabs}>
          {tabs.map((d) => (
            <button key={d.key} type="button" role="tab" aria-selected={tab === d.key} className={`${s.tab} ${tab === d.key ? s.on : ''}`} onClick={() => onTab(d.key)}>
              {d.label}
              {d.n ? <span className={s.n}>{d.n}</span> : null}
            </button>
          ))}
        </div>
        <button type="button" className={s.close} aria-label={closeLabel ?? t('shell.dockClose')} onClick={onClose}>
          ×
        </button>
      </div>
      {children}
    </div>
  );
}

/** Trạng thái bảng phải: mở sẵn ở khổ ≥ 1800 (dock.w = 372), đóng sẵn ở khổ nhỏ; mở / đóng bằng nút trên đầu trang. */
export function useDock<K extends string>(first: K) {
  const q = '(min-width: 1800px)';
  const [wide, setWide] = useState(() => typeof matchMedia === 'function' && matchMedia(q).matches);
  useEffect(() => {
    if (typeof matchMedia !== 'function') return;
    const mq = matchMedia(q);
    const on = () => setWide(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  const [pick, setPick] = useState<K | null | undefined>(undefined);
  const tab = pick === undefined ? (wide ? first : null) : pick;
  return {
    wide,
    open: tab !== null,
    tab: tab ?? first,
    /** bấm nút đầu trang: ≥ 1800 luôn mở (chỉ đổi tab), khổ nhỏ bấm lại thì đóng */
    toggle: (k: K) => setPick(tab === k && !wide ? null : k),
    show: (k: K) => setPick(k),
    close: () => setPick(null),
  };
}
